import { Dialog, DialogContent, DialogHeader, DialogTitle, Badge, ConfirmDialog } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import { formatDateTime } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import type { FormResponse } from "@tellescope/types-models";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import {
  useFormResponseDialogShallowStore,
  selectHasPendingFiles,
  type EnduserFormWizardProps,
} from "@tellescope/forms";
import { FormResponseSkeleton } from "./FormResponseSkeleton";
import { FormResponseError } from "./FormResponseError";
import { PendingFormContent } from "./PendingFormContent";
import { EmptyResponses } from "./EmptyResponses";
import { FormAnswer } from "./FormAnswer";

interface NativeFormResponseDialogProps {
  formResponse: FormResponse | null;
  titleOverride?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NativeFormResponseDialog = ({
  formResponse: openedFormResponse,
  titleOverride,
  open,
  onOpenChange,
}: NativeFormResponseDialogProps) => {
  const { t } = useTranslation("forms");
  const { pendingFilesByFieldId, reset: resetFormStore } = useFormResponseDialogShallowStore(
    "pendingFilesByFieldId",
    "reset"
  );
  const hasPendingFiles = selectHasPendingFiles(pendingFilesByFieldId);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

  const [chainedResponseId, setChainedResponseId] = useState<string | null>(null);

  const {
    data: chainedFormResponse,
    isError: chainedFormResponseFailed,
    refetch: refetchChainedFormResponse,
  } = useTellescopeQuery({
    model: "form_responses",
    method: "getOne",
    request: chainedResponseId ?? "",
    enabled: !!chainedResponseId,
  });

  const continueToFormInApp: EnduserFormWizardProps["continueToFormInApp"] = (prepared) => {
    resetFormStore();
    setChainedResponseId(prepared.id);
    return true;
  };

  const formResponse = (chainedResponseId ? chainedFormResponse : openedFormResponse) ?? null;

  const isSubmitted = !!formResponse?.submittedAt;
  const isPending = !!formResponse && !isSubmitted;

  const visibleResponses = formResponse?.responses?.filter((response) => {
    return response.sharedWithEnduser === true || isSubmitted;
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && hasPendingFiles) {
          setIsConfirmingDiscard(true);
          return;
        }
        if (!nextOpen) {
          resetFormStore();
          setChainedResponseId(null);
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="w-[90svw] max-w-[650px] sm:max-w-[650px] h-[85vh] flex flex-col p-0 gap-0"
        disableCloseOnClickOutside={isPending}
      >
        <DialogHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className="h-8 w-8 p-0 md:hidden"
              onClick={() => onOpenChange(false)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <DialogTitle className="text-base font-medium truncate pr-8">
              {chainedFormResponseFailed ? t("Form") : titleOverride ?? formResponse?.formTitle}
            </DialogTitle>
          </div>
        </DialogHeader>

        {formResponse && isSubmitted && (
          <div className="px-4 py-3 border-b bg-muted/30 flex-shrink-0">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>{t("Submitted {{timestamp}}", { timestamp: formatDateTime(formResponse.submittedAt!) })}</span>
            </div>

            {formResponse.scores && formResponse.scores.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formResponse.scores.map((score, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {score.title}: {score.value}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          {chainedFormResponseFailed ? (
            <FormResponseError onRetry={() => refetchChainedFormResponse()} onClose={() => onOpenChange(false)} />
          ) : !formResponse ? (
            <FormResponseSkeleton />
          ) : isPending ? (
            <PendingFormContent
              key={formResponse.id}
              formResponse={formResponse}
              onComplete={() => onOpenChange(false)}
              continueToFormInApp={continueToFormInApp}
              surface="form-response-dialog"
            />
          ) : !visibleResponses || visibleResponses.length === 0 ? (
            <EmptyResponses />
          ) : (
            <div className="p-4 space-y-4 overflow-y-auto h-full">
              {visibleResponses.map((response) => (
                <FormAnswer key={response.fieldId} title={response.fieldTitle} answer={response.answer} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      <ConfirmDialog
        open={isConfirmingDiscard}
        onOpenChange={setIsConfirmingDiscard}
        variant="warning"
        title={t("Discard selected files?")}
        description={t("You selected files that haven't been uploaded yet. Closing this form will discard them.")}
        confirmText={t("Discard and close")}
        cancelText={t("Keep editing")}
        onConfirm={() => {
          setIsConfirmingDiscard(false);
          resetFormStore();
          setChainedResponseId(null);
          onOpenChange(false);
        }}
      />
    </Dialog>
  );
};
